import React, {FC, useState} from 'react';
import classNames from "classnames";
import ArrowDownIcon from "../../common/icons/ArrowDownIcon";
import ArrowUpIcon from "../../common/icons/ArrowUpIcon";
import {pageAPI} from "../../../api/page";
import {wikiAPI} from "../../../api/wp";
import {formatDate, getDates, limit, normalize} from "../../../utils/convert";
import CloseIcon from "../../common/icons/CloseIcon";
import WikiIcon from "../../common/icons/WikiIcon";
import {Link, useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import PageHeader from "./PageHeader";
import endpoints from "../../../endpoints";
import HistoryIcon from "../../common/icons/HistoryIcon";
import PlusIcon from "../../common/icons/PlusIcon";
import {setPageAction, setRevisionAction} from "../../../store/reducers/searchSlice";
import IPage from "../../../types/page";
import LoadingIcon from "../../common/icons/LoadingIcon";
import {getCurrentYear} from "../../../utils/content";
import {setOptionsAction} from "../../../store/reducers/commonSlice";

const closeButtonClass = "ml-2 text-sm focus:outline-none text-white bg-red-300 hover:bg-red-800 focus:ring-4 focus:ring-red-300 rounded-[50%] font-medium text-sm"
const thClass = "select-none border-b font-medium px-2 pt-3 pb-3 text-slate-800 text-left bg-gray-100";
const tdClass = "border-b border-slate-300 dark:border-slate-700 px-2 pt-2 pb-2 text-slate-500";
const tableClass = "border-collapse table-fixed w-full text-sm mt-3";

type TListState = {
    field: string
    order: 'desc' | 'asc'
}

const statuses: {[key: number]: string} = {
    0: '',
    1: '(R)'
}

const currentYear = getCurrentYear()

const PageList = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const options = useAppSelector(state => state.common.options)

    const [getViewsData, {data: viewsData, isLoading: isViewsDataLoading, isSuccess: isViewsDataSuccess}] = wikiAPI.useGetViewsDataMutation()
    const [getRevisions, {data: revisions, isLoading: isRevisionsLoading, isSuccess: isRevisionsSuccess}] = wikiAPI.useGetRevisionsMutation()
    const [checkRedirect, {data: redirect, isLoading: isRedirectLoading, isSuccess: isRedirectSuccess}] = wikiAPI.useCheckRedirectMutation()

    const {data: pages} = pageAPI.useGetAllQuery({
        sort: options.tracingSortingField,
        order: options.tracingSortingType as "desc" | "asc",
        email: options.tracingFilterEmail ?? ''
    })
    const [deletePageAction, {}] = pageAPI.useRemoveMutation()

    const deletePage = (pageId: number) => {
        if (window.confirm("Are you sure?")) {
            deletePageAction(pageId)
        }
    }

    const isMonthly = () => {
        return (options.revisionsFilterPeriod === 'year' || options.revisionsFilterPeriod === 'lastYear') && !options.revisionsFilterMonthValue
    }

    const loadStatistic = async (page: IPage) => {
        navigate('/')

        dispatch(setRevisionAction(null))
        let searchValue = page.title

        let {data: pagesData} = await checkRedirect({
            titles: [page.title],
            withRedirect: true
        })
        if (!pagesData) return;

        searchValue = normalize(pagesData.page.title)

        dispatch(setPageAction({
            id: page.id,
            title: page.title,
            slug: searchValue,
            url: page.url,
            redirectedFrom: pagesData.page.redirectedFrom
        }))

        const dates = getDates(options.revisionsFilterPeriod, options.revisionsFilterValue ?? currentYear, options.revisionsFilterMonthValue)

        await getViewsData({
            query: searchValue,
            dateFrom: dates.from,
            dateTo: dates.to,
            type: isMonthly() ? 'monthly' : 'daily'
        })

        await getRevisions({
            pageId: pagesData.page.id,
            limit: options.revisionsStep
        })
    }

    return (
        <section className="mb-[15px]">
            <div className="container">
                <PageHeader />
                <div className="flex flex-row justify-between">
                    <table className={tableClass}>
                        <thead>
                            <tr>
                                <TH title="Page" field="title" />
                                <TH title="Email" field="email" />
                                <TH title="Last Revision" field="lastRevisionId" />
                                <TH title="Date" field="date" />
                                <TH />
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 w-[30px] h-[30px]">
                            {pages && pages.map(page => <tr className="hover:bg-blue-100" key={page.id}>
                                <td className={tdClass}>{limit(page.title, 30) + ` ${statuses[page.status]}`}</td>
                                <td className={tdClass}>{page.email}</td>
                                <td className={tdClass}>{page.lastRevisionId}</td>
                                <td className={tdClass}>{formatDate(page.date)}</td>
                                <td width="150" className={`${tdClass} text-right`}>
                                    <div className="flex">
                                        <span className="inline-block cursor-pointer" onClick={() => loadStatistic(page)} >
                                            <PlusIcon width={32} height={32} />
                                        </span>
                                        <Link to={page.url} target="_blank" className="inline-block ml-2">
                                            <WikiIcon />
                                        </Link>
                                        <Link to={endpoints.editsHistoryUrl.replace('{title}', page.title)} target="_blank" className="inline-block ml-2">
                                            <HistoryIcon />
                                        </Link>
                                        <button className={closeButtonClass + ' ml-2'} onClick={() => deletePage(page.id)}>
                                            <CloseIcon width={34} height={30} />
                                        </button>
                                    </div>
                                </td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

type TTHProps = {
    title?: string
    field?: string
}

const TH: FC<TTHProps> = ({title, field}) => {
    const dispatch = useAppDispatch()
    const options = useAppSelector(state => state.common.options)

    const changeSorting = (e: React.MouseEvent<HTMLSpanElement>) => {
        if (!field || !title) return;
        dispatch(setOptionsAction({
            tracingSortingField: field,
            tracingSortingType: options.tracingSortingField === field ? (
                options.tracingSortingType === 'asc' ? 'desc' : 'asc'
            ) : 'asc'
        }))
    }

    if (!field) {
        return <th className={`${thClass} w-[170px]`} />;
    }

    return (
        <th className={thClass}>
            <div
                className={classNames('flex select-none', {'cursor-pointer': field && title})}
                data-field={field}
                onClick={changeSorting}
            >
                <span className="mr-2 select-none">{title}</span>
                {options.tracingSortingField === field && (options.tracingSortingType === 'desc' ? <ArrowDownIcon /> : <ArrowUpIcon />)}
            </div>
        </th>
    )
}

export default PageList;